// A message's text drawn: the chat's Markdown (markdown.ts) as elements of the page, never as HTML — what
// a message holds shows as text, whatever it is. A user named shows by name, and lit when it is the user;
// hidden text shows when clicked; links open in a tab of their own. A text of a few emoji alone shows
// them large. The trees are kept for the texts drawn last: the page is drawn again often (an upload's
// progress), its messages seldom change.
import { useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { emojiOnly, parse, type Block, type Inline } from "../markdown.ts";
import { nameOf, state } from "../store.ts";

/** Texts and their trees, as many as the chat shows at most. */
const trees = new Map<string, Block[]>();
const KEPT = 1500;

function treeOf(text: string): Block[] {
  const t = trees.get(text);
  if (t !== undefined) return t;
  const made = parse(text);
  if (trees.size >= KEPT) trees.delete(trees.keys().next().value!);
  trees.set(text, made);
  return made;
}

/** Text `text` of a message, drawn. */
export function Rich({ text }: { text: string }) {
  const jumbo = emojiOnly(text);
  if (jumbo > 0) return <div class="rich jumbo">{text.trim()}</div>;
  return <div class="rich">{treeOf(text).map((b, i) => block(b, i))}</div>;
}

/** Text `text` in one line, its marks drawn — a quote of a message. */
export function RichLine({ text }: { text: string }) {
  const lines = treeOf(text)
    .map((b) => (b.t === "p" || b.t === "h" ? b.c : b.t === "list" ? b.items.flat() : b.t === "quote" ? [] : [{ t: "code" as const, v: b.v }]))
    .flat()
    .filter((x) => x.t !== "br");
  return <span class="rich-line">{spans(lines)}</span>;
}

function block(b: Block, key: number): ComponentChildren {
  switch (b.t) {
    case "p":
      return <p key={key}>{spans(b.c)}</p>;
    case "h":
      return b.level === 1 ? <h3 key={key}>{spans(b.c)}</h3> : b.level === 2 ? <h4 key={key}>{spans(b.c)}</h4> : <h5 key={key}>{spans(b.c)}</h5>;
    case "code":
      return (
        <pre key={key} class="code-block" data-lang={b.lang || undefined}>
          <code>{b.v}</code>
        </pre>
      );
    case "quote":
      return (
        <blockquote key={key}>{b.c.map((x, i) => block(x, i))}</blockquote>
      );
    case "list":
      return b.ordered ? (
        <ol key={key} start={b.start}>
          {b.items.map((item, i) => (
            <li key={i}>{spans(item)}</li>
          ))}
        </ol>
      ) : (
        <ul key={key}>
          {b.items.map((item, i) => (
            <li key={i}>{spans(item)}</li>
          ))}
        </ul>
      );
  }
}

function spans(c: Inline[]): ComponentChildren[] {
  return c.map((x, i) => span(x, i));
}

function span(x: Inline, key: number): ComponentChildren {
  switch (x.t) {
    case "text":
      return x.v;
    case "br":
      return <br key={key} />;
    case "code":
      return <code key={key}>{x.v}</code>;
    case "b":
      return <strong key={key}>{spans(x.c)}</strong>;
    case "i":
      return <em key={key}>{spans(x.c)}</em>;
    case "u":
      return <u key={key}>{spans(x.c)}</u>;
    case "s":
      return <s key={key}>{spans(x.c)}</s>;
    case "spoiler":
      return <Spoiler key={key}>{spans(x.c)}</Spoiler>;
    case "link":
      return (
        <a key={key} href={x.href} target="_blank" rel="noopener noreferrer nofollow" title={x.href}>
          {spans(x.c)}
        </a>
      );
    case "mention":
      return (
        <span key={key} class={x.user === state.user ? "mention me" : "mention"}>
          @{nameOf(x.user)}
        </span>
      );
  }
}

/** Hidden text: shown once clicked. */
function Spoiler({ children }: { children: ComponentChildren }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      class={open ? "spoiler open" : "spoiler"}
      title={open ? undefined : "Скрытый текст: нажмите, чтобы показать"}
      onClick={(e) => {
        if (!open) e.stopPropagation();
        setOpen(true);
      }}
    >
      {children}
    </span>
  );
}
